/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React from 'react'
import { css, jsx } from '@emotion/react'
import { Navigate, useParams } from 'react-router-dom'
import globoWebsiteId from '@waiterio/shared/globoWebsiteId.js'
import faviconUrl from '@waiterio/shared/faviconUrl.js'
import getDocuments from '@globo/client/getDocuments.js'
import useDocuments from '@globo/react/useDocuments.js'
import { useTranslation } from '@multilocale/react/index.js'
import Layout from '../components/LayoutComponent.js'
import languages from '../languages.js'

const documentStyle = css({
  maxWidth: 800,
  backgroundColor: 'white',
  margin: '15px auto 15px auto',
  padding: '43.5px 87px',

  h1: {
    fontSize: 25.2,
    lineHeight: 1.2,
    padding: '16px 0px',
  },

  'h2, h3, p, li, ul, i, b': {
    fontSize: 21.6,
    lineHeight: 1.2,
    textRendering: 'optimizeLegibility',
  },

  'ol, ul': {
    paddingLeft: 0,
  },

  ol: {
    counterReset: 'item',
  },

  'ol li': {
    counterIncrement: 'item',
    listStyle: 'none inside',
    listStylePosition: 'inside',
    textIndent: -35,
    paddingLeft: 49.5,
    paddingBottom: 14.5,
  },

  'ol li:before': {
    content: 'counter(item)',
    paddingRight: 14.5,
  },

  'ul li': {
    counterIncrement: 'item',
    listStylePosition: 'inside',
    paddingLeft: 14.5,
    paddingBottom: 14.5,
  },

  a: {
    textDecoration: 'underline',
    color: '#3f92d2',
    cursor: 'pointer',
    '&:hover': {
      color: '#5cb85c',
    },
  },
  '@media (max-width: 768px)': {
    padding: 20,
    fontSize: 24,
  },
})

const errorStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 160,
  fontSize: 36,
  color: 'red',
  padding: 36,
})

export const getStaticPaths = async () => {
  let documents = await getDocuments({
    websiteId: globoWebsiteId,
    fields: ['_id', 'locale', 'slug'],
  })

  return documents.map(({ locale, slug }) => `/docs/${locale}/${slug}`).sort()
}

export const Head = ({ locale }) => {
  const { slug } = useParams()
  const { t } = useTranslation(locale)

  let { data: documents } = useDocuments({
    websiteId: globoWebsiteId,
    slug,
    fields: ['_id', 'creationTime', 'lastEditTime', 'title', 'slug', 'content'],
  })

  const document = documents?.[0]

  const title = document?.title ?? ''
  const description = `${t('Documentation')} | ${t('Waiterio Restaurant POS')}`
  let canonical = `https://www.waiterio.com/docs/${locale}/${slug}/`

  if (document) {
    canonical = `https://www.waiterio.com/docs/${locale}/${document.slug}/`
  }

  return (
    <>
      <title>{`${title} | ${description}`}</title>
      <link rel="canonical" href={canonical} />
      <meta name="description" content={description} />
      <meta name="robots" content="max-image-preview:large" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={faviconUrl} />
      <meta property="og:site_name" content={title} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://www.waiterio.com/docs/" />
      <meta property="og:image" content={faviconUrl} />
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      <meta itemProp="image" content={faviconUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'QAPage',
            inLanguage: locale,
            mainEntity: {
              '@type': 'Question',
              name: document?.title,
              text: document?.title,
              inLanguage: locale,
              dateCreated: new Date(
                document?.creationTime ?? null,
              )?.toISOString(),
              dateModified: new Date(
                document?.lastEditTime ?? null,
              )?.toISOString(),
              acceptedAnswer: {
                '@type': 'Answer',
                text: document?.content,
                url: canonical,
                inLanguage: locale,
                dateCreated: new Date(
                  document?.creationTime ?? null,
                )?.toISOString(),
                dateModified: new Date(
                  document?.lastEditTime ?? null,
                )?.toISOString(),
                upvoteCount: 1,
                author: {
                  '@id': 'https://www.waiterio.com/#corporation',
                },
                publisher: {
                  '@id': 'https://www.waiterio.com/#corporation',
                },
              },
              answerCount: 1,
              author: {
                '@id': 'https://www.waiterio.com/#corporation',
              },
              publisher: {
                '@id': 'https://www.waiterio.com/#corporation',
              },
            },
          }),
        }}
      />
    </>
  )
}

export const paths = languages.map(locale => ({
  path: `/docs/${locale}/:slug/`,
  props: {
    locale,
  },
}))

const DocumentPage = ({ locale }) => {
  const { slug } = useParams()
  const {
    data: documents,
    error,
    loading,
  } = useDocuments({
    websiteId: globoWebsiteId,
    slug,
    fields: ['_id', 'creationTime', 'lastEditTime', 'title', 'slug', 'content'],
  })

  const document = documents?.[0]

  return (
    <Layout locale={locale}>
      <div css={{ height: 76, minHeight: 76 }} />
      {documents?.length === 0 && (
        <Navigate to={`/docs/${locale}/not-found/`} />
      )}
      <div
        css={{
          backgroundColor: '#f5f7fb',
          display: 'flex',
          flexDirection: 'column',
          padding: 16,
          margin: 16,
        }}
      >
        {document && (
          <div css={documentStyle}>
            <h1>{document.title}</h1>
            <article dangerouslySetInnerHTML={{ __html: document.content }} />
          </div>
        )}

        {loading && <div className="loader" />}

        {error && <div css={errorStyle}>{error}</div>}
      </div>
    </Layout>
  )
}

export default DocumentPage
